import { usePreventClose } from '@/common/hooks/usePreventClose'
import { useSaveOrgWhiteboardMutation } from '@gql'
import { useEffect, useMemo, useState } from 'react'
import { createWhiteboardSaver, WhiteboardSaveState } from '../whiteboardSaver'

// Saves the org's whiteboard as it is drawn (#200): after each pause, again
// until accepted, at once when the view closes or the tab is hidden; and the
// browser asks before leaving while a change is not saved.
export default function useWhiteboardSave<Scene>(orgId: string | undefined) {
  const [saveOrgWhiteboard] = useSaveOrgWhiteboardMutation()
  const { preventClose, allowClose } = usePreventClose()
  const [state, setState] = useState<WhiteboardSaveState>('idle')
  const [savedId, setSavedId] = useState<string>()

  const saver = useMemo(() => {
    if (!orgId) return
    return createWhiteboardSaver<Scene>({
      send: async (elements) => {
        const { data } = await saveOrgWhiteboard({
          variables: { orgId, elements },
        })
        const id = data?.insert_org_whiteboard_one?.id
        if (!id) throw new Error('Whiteboard not saved')
        setSavedId(id)
      },
      onStateChange: setState,
    })
  }, [orgId])

  // Leaving the view or the org: send what is left
  useEffect(() => {
    if (!saver) return
    const onHide = () => {
      if (document.visibilityState === 'hidden') saver.flush()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      // Not disposed: a send refused while offline keeps being retried after
      // the view is gone, and the browser still warns before closing the tab
      // until it is saved
      saver.flush().then(() => {
        if (!saver.hasUnsaved()) return
        const guard = (event: BeforeUnloadEvent) => {
          if (!saver.hasUnsaved()) {
            window.removeEventListener('beforeunload', guard)
            return
          }
          event.preventDefault()
          event.returnValue = ''
        }
        window.addEventListener('beforeunload', guard)
      })
    }
  }, [saver])

  useEffect(() => {
    if (state === 'idle' || state === 'saved') allowClose()
    else preventClose()
  }, [state])

  return { saver, state, savedId }
}
